// Story 19: selecting an account with a saved sizing restores every slice, including the Tool
// consolidation checklist. Fetches the account's latest snapshot and reads its Story_JSON__c back
// through the migration (migrateSavedStory), so a sizing saved by the v2 calculator opens with its
// re-check flags and a v3 one opens exactly as saved.

import { fetchSnapshots } from '../api-client';
import type { AccountSelection } from '../flow/step-model';
import { loadFromSavedStory, type LoadedSizing } from './load-paths';

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export type RestoreResult =
  /** The account has no saved sizing yet. */
  | { kind: 'none' }
  | { kind: 'restored'; loaded: LoadedSizing; savedAt: string | null }
  /** A snapshot exists but its story could not be read (unknown version, corrupt JSON). */
  | { kind: 'unreadable'; savedAt: string | null };

/** Fetch and migrate the latest saved sizing for a linked account. Rejects when the fetch fails. */
export async function fetchSavedSizing(
  apiFetch: ApiFetch,
  account: Extract<AccountSelection, { kind: 'account' }>,
  step?: string,
): Promise<RestoreResult> {
  const { latest } = await fetchSnapshots(apiFetch, account.accountId);
  if (!latest) return { kind: 'none' };
  const loaded = loadFromSavedStory(latest.storyJson, account, step);
  return loaded
    ? { kind: 'restored', loaded, savedAt: latest.createdDate }
    : { kind: 'unreadable', savedAt: latest.createdDate };
}
